/**
 * FormattedNumber.js
 * Renders a number with the integer part in normal size and the decimal part
 * in a smaller font size (70% by default), creating a visual hierarchy.
 *
 * Usage:
 *   <FormattedNumber value={1066202.5} />
 *   <FormattedNumber value={1440} decimals={2} />
 *   <FormattedNumber value={1066202.5} prefix="SAR " />
 *   <FormattedNumber value={12.5} suffix="%" decimalSize="0.65em" />
 *
 * Props:
 *   value       {number|string}  The number to format
 *   decimals    {number}         Decimal places (default: 2)
 *   prefix      {string}         Text before the number (e.g. "SAR ")
 *   suffix      {string}         Text after the number (e.g. "%")
 *   decimalSize {string}         Font size for decimal part (default: "0.7em")
 *   className   {string}         Optional CSS class for the outer span
 *   style       {object}         Optional inline style for the outer span
 */

const FormattedNumber = ({
  value,
  decimals = 2,
  prefix = "",
  suffix = "",
  decimalSize = "0.7em",
  className,
  style,
}) => {
  const num = parseFloat(value);
  if (isNaN(num)) {
    return <span className={className} style={style}>{prefix}—{suffix}</span>;
  }

  // Format with fixed decimal places
  const formatted = num.toLocaleString("en-US", {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });

  // Split on decimal point
  const dotIndex = formatted.lastIndexOf(".");
  if (dotIndex === -1 || decimals === 0) {
    // No decimal part
    return (
      <span className={className} style={style}>
        {prefix}{formatted}{suffix}
      </span>
    );
  }

  const integerPart = formatted.slice(0, dotIndex);
  const decimalPart = formatted.slice(dotIndex); // includes the "."

  return (
    <span className={className} style={style}>
      {prefix}
      {integerPart}
      <span style={{ fontSize: decimalSize, verticalAlign: "baseline", opacity: 0.85 }}>
        {decimalPart}
      </span>
      {suffix}
    </span>
  );
};

export default FormattedNumber;
